import * as Sentry from '@sentry/react-native';
import * as Notifications from 'expo-notifications';
import { router, Stack, useNavigationContainerRef } from 'expo-router';
import Head from 'expo-router/head';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import {
  APP_BASE_URL,
  DEFAULT_OG_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_TITLE,
} from '@/constants/og';
import { useAppFonts } from '@/hooks/useAppFonts';
import { useOtaUpdates } from '@/hooks/useOtaUpdates';
import { captureInitialReferral, subscribeToReferralLinks } from '@/lib/pendingReferral';
import { deliveryIdFromNotificationData, routeForNotificationData } from '@/lib/pushRouting';
import { isForeignNoise } from '@/lib/sentryFilter';
import { syncPushRegistration } from '@/services/pushNotifications';
import { pushOpensService } from '@/services/pushOpens';
import { authStore } from '@/stores/auth.store';
import { dealsStore } from '@/stores/deals.store';
import { giftingStore } from '@/stores/gifting.store';
import { snippetsStore } from '@/stores/snippets.store';
import { voltrStore } from '@/stores/voltr.store';
import { voltrUploadStore } from '@/stores/voltrUpload.store';
import { UploadStayFrame } from '@/components/social/UploadStayFrame';

/**
 * App Store ID from MarketPryce Rewards in App Store Connect (memory:
 * project_marketpryce_apple_ids). Used by the Apple Smart App Banner
 * meta tag below — Safari on iOS renders a native banner above the
 * page when this is present.
 */
const APP_STORE_ID = '6767093791';

const sentryDsn = process.env.EXPO_PUBLIC_SENTRY_DSN;
const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true,
});

Sentry.init({
  dsn: sentryDsn,
  enabled: !__DEV__ && !!sentryDsn,
  tracesSampleRate: 0.1,
  integrations: [navigationIntegration],
  // Drop third-party / benign noise (in-app-browser bridges, share-sheet
  // cancels, bare network "Load failed") before it reaches Sentry. Our own
  // ApiError-derived errors are always kept — see lib/sentryFilter.ts.
  beforeSend(event, hint) {
    return isForeignNoise(event, hint) ? null : event;
  },
});

// Foreground display behaviour for incoming pushes. Registered at module scope
// so it's in place before any notification can arrive. Native-only — web has no
// push in v1.
if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  });
}

/**
 * Route to the screen a tapped notification points at (from its data.target),
 * and tell the server it was opened.
 *
 * Both tap paths funnel through here — the warm listener and the cold-start
 * lookup — so the open is logged wherever the tap came from.
 */
function handleNotificationResponse(
  response: Notifications.NotificationResponse | null,
): void {
  if (!response) return;
  // Everything stays inside the try, including reading the payload. Navigation
  // is the user-visible half of a tap: if a malformed response makes the read
  // throw, a throw in front of router.push means the tap silently does nothing.
  try {
    const data = response.notification.request.content.data;
    logNotificationOpen(data);
    router.push(routeForNotificationData(data));
  } catch (err) {
    if (__DEV__) console.warn('[push] failed to route notification tap', err);
  }
}

/**
 * Fire-and-forget open logging. Routing must never wait on it or break because
 * of it — a dropped analytics write is invisible, a tap that doesn't navigate
 * is not.
 *
 * Gated on the TOKEN, not on `isAuthenticated`. That getter is
 * `sessionToken && user`, and `user` only lands after hydrate's `me()` call
 * returns — so gating on it drops real opens two ways. A push tapped while that
 * request is still in flight would be skipped even though apiClient already has
 * a working token installed. Worse, if `me()` fails for any reason that isn't a
 * 401 (offline, timeout, 5xx), `user` stays null for the whole JS session and
 * hydrate never retries, so every open is dropped while navigation keeps
 * working — quietly biasing the open rate down for exactly the flaky-network
 * users, and reading as low engagement rather than lost telemetry. A token is
 * the real precondition for the request succeeding.
 *
 * The 401-clears-your-session hazard is handled where it actually lives, in
 * `services/api.ts` via `suppressUnauthenticated`. An auth check here could
 * never have covered it anyway: a token the client believes in can still have
 * been revoked server-side.
 */
function logNotificationOpen(data: unknown): void {
  if (!authStore.sessionToken) return;

  const deliveryId = deliveryIdFromNotificationData(data);
  if (deliveryId === null) return; // sent before open tracking shipped

  void pushOpensService.logOpen(deliveryId).catch((err) => {
    if (__DEV__) console.warn('[push] failed to log notification open', err);
  });
}

SplashScreen.preventAutoHideAsync().catch(() => {
  /* already-prevented is fine */
});

const RootLayout = observer(() => {
  const { loaded: fontsLoaded } = useAppFonts();
  const navigationRef = useNavigationContainerRef();
  const coldStartHandled = useRef(false);

  // Download OTA updates when the app returns after ≥5 min in the background;
  // applied on the next cold start (never reloads mid-session). No-op in
  // dev / Expo Go / web.
  useOtaUpdates();

  useEffect(() => {
    if (navigationRef) {
      navigationIntegration.registerNavigationContainer(navigationRef);
    }
  }, [navigationRef]);

  useEffect(() => {
    authStore.hydrate();
    void captureInitialReferral().catch((err) => {
      if (__DEV__) console.warn('[referral] failed to capture initial referral', err);
    });
    return subscribeToReferralLinks();
  }, []);

  const authHydrated = authStore.hydrated;
  const isAuthenticated = authStore.isAuthenticated;

  useEffect(() => {
    if (!authHydrated) return;
    if (!isAuthenticated) {
      snippetsStore.reset();
      // Clears the shipping draft too — a second athlete on a shared phone
      // must not inherit the previous user's address.
      giftingStore.reset();
      // The previous user's Actions go, and a reply to their request that
      // lands later is dropped (dealsStore's session epoch).
      dealsStore.reset();
      voltrStore.reset();
      voltrUploadStore.reset();
      return;
    }
    // Wait for hydrate() to install the bearer token before firing this
    // authed request — otherwise a 401 here would clear the session.
    void snippetsStore.ensureLoaded();
  }, [authHydrated, isAuthenticated]);

  // Warm taps: user taps a push while the app is foreground/backgrounded.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const sub = Notifications.addNotificationResponseReceivedListener(handleNotificationResponse);
    return () => sub.remove();
  }, []);

  // Once auth is ready: silent token refresh, cold-start deep link (the push
  // that launched the app), and — only if no push launched it — the one-time
  // existing-user priming prompt.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (!authHydrated || !isAuthenticated) return;

    void syncPushRegistration();

    if (coldStartHandled.current) return;
    coldStartHandled.current = true;

    // If a push launched the app, route to its target. Priming is no longer
    // handled here — the home tab prompts once on first view (see
    // app/(tabs)/index.tsx), which keeps it off the signup/deal path.
    void Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        if (response) handleNotificationResponse(response);
      })
      .catch((err) => {
        if (__DEV__) console.warn('[push] cold-start lookup failed', err);
      });
  }, [authHydrated, isAuthenticated]);

  const ready = fontsLoaded && authStore.hydrated;

  useEffect(() => {
    if (ready) {
      SplashScreen.hideAsync().catch(() => {});
    }
  }, [ready]);

  return (
    <>
      {/* Head MUST render unconditionally (before the !ready guard) so
          static export captures these tags in the pre-rendered HTML.
          Otherwise OG/Twitter scrapers see an empty <head> because the
          export pre-renders the initial tree where ready=false. */}
      <Head>
        <title>{DEFAULT_OG_TITLE}</title>
        <meta name="description" content={DEFAULT_OG_DESCRIPTION} />
        <meta name="theme-color" content="#1B4030" />

        {/* Apple Smart App Banner — iOS Safari renders a native "Get
            the app" banner above the page. Silent no-op on every
            other browser. */}
        <meta name="apple-itunes-app" content={`app-id=${APP_STORE_ID}`} />

        {/* Open Graph defaults — per-page Head can override these. */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="MarketPryce" />
        <meta property="og:title" content={DEFAULT_OG_TITLE} />
        <meta property="og:description" content={DEFAULT_OG_DESCRIPTION} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="1024" />
        <meta property="og:url" content={APP_BASE_URL} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@marketpryce" />
        <meta name="twitter:title" content={DEFAULT_OG_TITLE} />
        <meta name="twitter:description" content={DEFAULT_OG_DESCRIPTION} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Head>
      {/* Stack mounts unconditionally so the per-route `<Head>` (notably
          `app/[slug].tsx`) actually renders during `expo export --platform
          web`. Gating it on `ready` would defer mount to after fonts +
          auth hydrate — neither happen at static-export time — and ship
          every slug page with only this layout's generic OG meta, which
          is what social scrapers would then read. The splash screen
          hides on its own once `ready` flips; SafeAreaProvider + Stack
          are both safe to mount pre-hydrate (the navigator just shows
          whatever screen matches the path). */}
      <GestureHandlerRootView style={{ flex: 1 }}>
        <SafeAreaProvider>
          <StatusBar style="dark" />
          <UploadStayFrame>
            <Stack screenOptions={{ headerShown: false }}>
              <Stack.Screen name="(auth)" />
              <Stack.Screen name="(onboarding)" />
              <Stack.Screen name="(tabs)" />
              {/* Push priming: overlays the home tab and dismisses back to it,
                  so it never redirects into another flow. */}
              <Stack.Screen name="notifications-priming" options={{ presentation: 'fullScreenModal' }} />
              <Stack.Screen name="+not-found" />
            </Stack>
          </UploadStayFrame>
        </SafeAreaProvider>
      </GestureHandlerRootView>
    </>
  );
});

export default Sentry.wrap(RootLayout);
